// ═══════════════════════════════════════════════════════════════
// RELATIONSHIP SERVICE
// Path: src/services/relationshipService.js
//
// CRUD for member_relationships table.
// Handles bidirectional auto-creation for spouse and child/parent pairs.
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

// Inverse relationship types for auto-pairing
const INVERSE_TYPES = {
  spouse: "spouse",
  child: "parent",
  parent: "child",
};

const fromDb = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    memberId: row.member_id,
    relatedMemberId: row.related_member_id,
    relationshipType: row.relationship_type,
    notes: row.notes || "",
    createdAt: row.created_at,
    // Joined member data (when available)
    relatedMember: row.related_member
      ? {
          id: row.related_member.id,
          firstName: row.related_member.first_name,
          lastName: row.related_member.last_name,
          organization: row.related_member.organization,
          donorType: row.related_member.donor_type,
        }
      : null,
  };
};

export const relationshipService = {
  /**
   * Get all relationships for a member (both directions)
   */
  async getByMember(memberId) {
    // Get relationships where this member is the source
    const { data: outgoing, error: err1 } = await supabase
      .from("member_relationships")
      .select("*, related_member:related_member_id(id, first_name, last_name, organization, donor_type)")
      .eq("member_id", memberId);

    if (err1) {
      debugLog.error("relationshipService.getByMember outgoing", err1);
      throw err1;
    }

    return (outgoing || []).map(fromDb);
  },

  /**
   * Create a relationship (auto-creates inverse for spouse/child/parent)
   */
  async create(memberId, relatedMemberId, relationshipType, notes = "") {
    const { data, error } = await supabase
      .from("member_relationships")
      .insert({
        member_id: memberId,
        related_member_id: relatedMemberId,
        relationship_type: relationshipType,
        notes,
      })
      .select()
      .single();

    if (error) {
      debugLog.error("relationshipService.create", error);
      throw error;
    }

    // Auto-create inverse relationship if applicable
    const inverseType = INVERSE_TYPES[relationshipType];
    if (inverseType) {
      const { error: invError } = await supabase
        .from("member_relationships")
        .insert({
          member_id: relatedMemberId,
          related_member_id: memberId,
          relationship_type: inverseType,
          notes,
        });

      if (invError && invError.code !== "23505") {
        // 23505 = unique violation (inverse already exists) — that's fine
        debugLog.error("relationshipService.create inverse", invError);
      }
    }

    debugLog.action("Relationship created", { memberId, relatedMemberId, relationshipType });
    return fromDb(data);
  },

  /**
   * Remove a relationship (auto-removes inverse for spouse/child/parent)
   */
  async remove(relationshipId) {
    // Fetch the relationship first to know the inverse
    const { data: rel, error: fetchErr } = await supabase
      .from("member_relationships")
      .select("*")
      .eq("id", relationshipId)
      .single();

    if (fetchErr) {
      debugLog.error("relationshipService.remove fetch", fetchErr);
      throw fetchErr;
    }

    // Delete the primary relationship
    const { error } = await supabase
      .from("member_relationships")
      .delete()
      .eq("id", relationshipId);

    if (error) {
      debugLog.error("relationshipService.remove", error);
      throw error;
    }

    // Auto-remove inverse if applicable
    const inverseType = INVERSE_TYPES[rel.relationship_type];
    if (inverseType) {
      await supabase
        .from("member_relationships")
        .delete()
        .eq("member_id", rel.related_member_id)
        .eq("related_member_id", rel.member_id)
        .eq("relationship_type", inverseType);
    }

    debugLog.action("Relationship removed", { id: relationshipId });
  },
};
